import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react'
import { AnimatePresence, motion } from 'motion/react'

interface AvisoActual {
  id: number
  texto: string
  accion?: { label: string; onClick: () => void }
}

type Avisar = (texto: string, accion?: AvisoActual['accion']) => void

const AvisoContext = createContext<Avisar>(() => {})

/** Un aviso breve encima de la barra inferior, con un "Deshacer" opcional. */
export function useAviso(): Avisar {
  return useContext(AvisoContext)
}

export function AvisoProvider({ children }: { children: ReactNode }) {
  const [aviso, setAviso] = useState<AvisoActual | null>(null)
  const contador = useRef(0)

  const avisar = useCallback<Avisar>((texto, accion) => {
    contador.current += 1
    setAviso({ id: contador.current, texto, accion })
  }, [])

  useEffect(() => {
    if (!aviso) return
    const timer = window.setTimeout(() => setAviso(null), 6000)
    return () => window.clearTimeout(timer)
  }, [aviso])

  return (
    <AvisoContext.Provider value={avisar}>
      {children}
      <div
        className="pointer-events-none fixed inset-x-0 bottom-[calc(5.5rem+env(safe-area-inset-bottom))] z-[60] flex justify-center px-4"
        aria-live="polite"
      >
        <AnimatePresence>
          {aviso && (
            <motion.div
              key={aviso.id}
              initial={{ opacity: 0, y: 16, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 8, scale: 0.98 }}
              transition={{ type: 'spring', stiffness: 420, damping: 32 }}
              className="pointer-events-auto flex max-w-md items-center gap-3 rounded-2xl bg-aviso py-2.5 pl-4 pr-2 text-sm text-aviso-foreground shadow-lg ring-1 ring-white/5"
            >
              <span className="min-w-0 flex-1">{aviso.texto}</span>
              {aviso.accion && (
                <button
                  type="button"
                  onClick={() => {
                    aviso.accion?.onClick()
                    setAviso(null)
                  }}
                  className="label-caps shrink-0 rounded-full px-3 py-2.5 text-apricot outline-none hover:bg-white/10 focus-visible:ring-2 focus-visible:ring-apricot"
                >
                  {aviso.accion.label}
                </button>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </AvisoContext.Provider>
  )
}
